import type { ReactNode } from "react";
import type { SupplyBill } from "../api/types.js";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { longDate } from "../lib/format.js";
import { DeleteAction } from "./DeleteAction.jsx";
import { Async, Failure } from "./Async.jsx";

/**
 * The household's electricity bills, sent from the field app beside the MPAN
 * (migration 0080): on the Energy section, because that is where the MPAN they
 * prove is read.
 *
 * A bill names a customer and holds their account details, so the file is
 * fetched only when somebody asks to open it, and the list says nothing about
 * what is on it. It cannot be edited from here and never could: 0081 grants
 * the three recycle-bin columns and nothing else.
 *
 * Deleting one is here because this is where a bill that reached the wrong
 * survey is noticed, and that was the gap worth closing: until 0081 nobody,
 * the owner included, could take it back.
 */
export function SupplyBills({ surveyId }: { readonly surveyId: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<SupplyBill[]>(() => client.supplyBills(surveyId), [client, surveyId]);
  return (
    <section className="site-survey__group" data-testid="supply-bills">
      <h2>Electricity bills</h2>
      <Async state={state} what="the electricity bills" reload={reload}>
        {(bills) =>
          bills.length === 0 ? (
            <p className="muted small">No bill has been sent from the phone for this survey.</p>
          ) : (
            <BillList surveyId={surveyId} bills={bills} reload={reload} />
          )
        }
      </Async>
    </section>
  );
}

function BillList({
  surveyId,
  bills,
  reload,
}: {
  readonly surveyId: string;
  readonly bills: readonly SupplyBill[];
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { submit, error } = useSubmit();

  const open = (bill: SupplyBill): void => {
    // Inside the click, or the browser takes the new tab for a pop-up: the
    // file has to be fetched with the session, so it cannot be a plain link.
    const tab = window.open("", "_blank");
    void submit(async () => {
      try {
        const url = await client.supplyBillObjectUrl(surveyId, bill.id);
        if (tab) tab.location.href = url;
        else window.location.assign(url);
        // Long enough for the tab to load it; a bill should not linger.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (cause) {
        tab?.close();
        throw cause;
      }
    });
  };

  return (
    <>
      <ul className="plain-list">
        {bills.map((bill) => (
          <li key={bill.id} className="row-between">
            <span>
              <strong>{bill.filename}</strong>
              <span className="small muted">
                {" "}
                — {fileSize(bill.bytes)}, sent {longDate(bill.uploadedAt)}
                {bill.uploadedByName ? ` by ${bill.uploadedByName}` : ""}.
              </span>
            </span>
            <span className="row-actions">
              <button type="button" className="secondary" onClick={() => open(bill)}>
                Open
              </button>
              <DeleteAction
                what="electricity bill"
                warning="It names the customer, so deleting it is often the right answer when it reached the wrong survey."
                onDelete={(reason) => client.deleteSupplyBill(surveyId, bill.id, reason)}
                onDone={reload}
              />
            </span>
          </li>
        ))}
      </ul>
      <Failure error={error} what="that bill" />
    </>
  );
}

function fileSize(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
